<template>
  <popup-base class="phone-change-popup" @close="closePopup">
    <template v-if="!successPhoneConfirmation">
      <template slot="header">
        Phone change
        <stages-legend
          :class="{'stages-legend_stage2': stages[1].active}"
          class="phone-change-popup__stages-legend"
          :stages="stages"
        />
      </template>
      <div slot="body" class="phone-change-popup__wrap">
        <template v-if="stages[0].active">
          <div class="phone-change-popup__notice">
            Please input new phone number in&nbsp;the&nbsp;field below.<br class="new-liner" />
            We&nbsp;will send you SMS&nbsp;with confirmation code.
          </div>
          <form novalidate @submit.stop.prevent="tryToChangePhoneNumber">
            <phone-input
              class="phone-change__phone"
              type="tel"
              name="phone"
              inputId="phone"
              placeholder="Enter your phone"
              label="Phone"
              @input="serverFieldErrors.phone = null"
              v-model="$v.phone.$model"
              :error="$v.phone.$error"
            />
            <button-el red large left :disabled="requestInProgress">Continue
              <icon-base red>
                <icon-arrow-min />
              </icon-base>
            </button-el>
            <round-btn small @click="closePopup" class="close-btn">
              <icon-base red>
                <icon-cross />
              </icon-base>
            </round-btn>
          </form>
        </template>
        <template v-if="stages[1].active">
          <div class="phone-change-popup__notice">
            Please input confirmation code from<br class="new-liner" />
            SMS in&nbsp;the&nbsp;field below.
          </div>
          <form novalidate @submit.stop.prevent="tryToConfirmPhoneNumber">
            <input-el
              class="phone-change__code popup-input"
              inline
              type="text"
              name="code"
              inputId="code"
              placeholder="Enter your code"
              label="Code"
              @input="serverFieldErrors.code = null"
              v-model.trim="$v.code.$model"
              :error="$v.code.$error"
            />
            <div
              class="phone-change__resend"
              :class="{
                'phone-change__resend_disabled': resendIsDisabled,
                'phone-change__resend_sent': secondsToResend,
              }"
            >
              <span class="phone-change__resend-text phone-change__resend-text_sent" v-if="secondsToResend">
                We're able to&nbsp;send new code in&nbsp;{{secondsToResend}}&nbsp;s
              </span>
              <span class="phone-change__resend-text" v-else>
                Didn’t receive the code? Resend.
              </span>
              <round-btn type="button" small class="phone-change__resend-btn" :disabled="resendIsDisabled" @click="resendCode">
                <icon-base red>
                  <icon-refresh />
                </icon-base>
              </round-btn>
            </div>
            <button-el class="back-btn" :disabled="requestInProgress" type="button" @click="goPrevStage(false)" black large left>
              Back
              <icon-base slot="icon" black>
                <icon-arrow-min left />
              </icon-base>
            </button-el>
            <button-el class="btn-left" :disabled="requestInProgress" red large left>Confirm
              <icon-base red>
                <icon-check />
              </icon-base>
            </button-el>
            <round-btn small @click="closePopup" class="close-btn">
              <icon-base red>
                <icon-cross />
              </icon-base>
            </round-btn>
          </form>
        </template>
      </div>
    </template>

    <!-- success -->
    <template v-else>
      <template slot="header">
        Success!
      </template>
      <div slot="body" class="phone-change-popup__wrap">
        <div class="phone-change-popup__notice phone-change-popup__notice_confirmation">
          You successfully changed your phone number!
        </div>
        <button-el red large left @click.native="closePopup">Close
          <icon-base red>
            <icon-check />
          </icon-base>
        </button-el>
      </div>
    </template>
  </popup-base>
</template>

<script>
import stagesSwitchersMixin from 'src/mixins/stagesSwitchers';
import validateFormMixin from 'src/mixins/validateForm';
import { required, minLength } from 'vuelidate/lib/validators';
import { IconCheck, IconArrowMin, IconRefresh, IconCross } from 'Icons';
import PhoneInput from 'Uikit/PhoneInput';
import PopupBase from 'Common/PopupBase';

let resendCounterTimerId;

export default {
  mixins: [
    validateFormMixin,
    stagesSwitchersMixin,
  ],
  data: () => ({
    requestInProgress: false,
    successPhoneConfirmation: false,
    phone: '',
    code: '',
    secondsToResend: 0,
    serverFieldErrors: {
      phone: {},
      code: {},
    },
    stages: [
      {
        name: 'New number',
        finished: false,
        active: true,
      },
      {
        name: 'Validation',
        finished: false,
        active: false,
      },
    ],
  }),
  validations: {
    phone: {
      required,
      minLength: minLength(5),
      serverError() {
        return !(this.serverFieldErrors && this.serverFieldErrors.phone);
      },
    },
    code: {
      required,
      serverError() {
        return !(this.serverFieldErrors && this.serverFieldErrors.code);
      },
    },
  },
  components: {
    IconCheck,
    IconCross,
    IconArrowMin,
    IconRefresh,
    PhoneInput,
    PopupBase,
  },
  computed: {
    resendIsDisabled() {
      return this.requestInProgress || this.secondsToResend > 0;
    },
  },
  methods: {
    closePopup() {
      if (this.successPhoneConfirmation) {
        this.$emit('closePopupWithSucccess');
        return;
      }
      this.$emit('closePopupWithNoResult');
    },
    resendCode() {
      this.changePhoneNumber();
    },
    tryToChangePhoneNumber() {
      this.validateForm('phone', () => this.changePhoneNumber().then(() => this.goNextStage(false)));
    },
    tryToConfirmPhoneNumber() {
      this.validateForm('code', this.confirmPhoneNumber);
    },
    changePhoneNumber() {
      this.requestInProgress = true;
      return this.$apiService.changePhoneNumber(`+${this.phone}`).then(() => {
        this.startResendCounter();
      }).catch((errors) => {
        if (errors.fieldErrors) {
          this.serverFieldErrors = errors.fieldErrors;
        }
        return Promise.reject();
      }).finally(() => {
        this.requestInProgress = false;
      });
    },
    startResendCounter() {
      this.secondsToResend = 30;
      if (resendCounterTimerId) {
        clearTimeout(resendCounterTimerId);
      }
      this.decreaseResendCounter();
    },
    decreaseResendCounter() {
      resendCounterTimerId = setTimeout(() => {
        this.secondsToResend -= 1;
        if (this.secondsToResend <= 0) {
          return;
        }
        this.decreaseResendCounter();
      }, 1000);
    },

    confirmPhoneNumber() {
      this.requestInProgress = true;
      return this.$apiService.confirmPhoneNumber(`+${this.phone}`, this.code).then(() => {
        this.successPhoneConfirmation = true;
      }).catch((errors) => {
        if (!errors.fieldErrors) {
          return;
        }
        this.serverFieldErrors = errors.fieldErrors;
        if (errors.fieldErrors.phone) {
          this.goPrevStage(false);
        }
      }).finally(() => {
        this.requestInProgress = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  $side-margin: 430px;
  .phone-change-popup {
    &__stages-legend {
      margin-top: 37px;
      margin-bottom: -5px;
    }
    &__body {
      max-width: $phone-change-popup-max-width;
    }
    &__wrap {
      padding: 35px 30px 30px;
      width: $phone-change-popup-max-width;
    }
    &__notice {
      @include font(13px, $black-color1);
      margin-bottom: 25px;
      line-height: 21px;
      &_confirmation {
        margin-bottom: 40px;
        line-height: 21px;
      }
    }
  }
  .phone-change {
    &__code {
      margin-bottom: 24px;
    }
    &__phone {
      margin-bottom: 59px;
    }
    &__resend {
      display: flex;
      align-items: center;
      margin-bottom: 55px;
      justify-content: space-between;
      max-width: 249px;
      &_sent {
        max-width: 270px;
      }
      &_disabled {
        color: $grey-color2;
      }
    }
    &__resend-btn {
      &:hover {
        animation-duration: $transition * 5;
        animation-name: infinityRotate;
        animation-iteration-count: infinite;
        animation-direction: normal;
        animation-timing-function: linear;
      }
      &[disabled]:hover {
        animation: none;
      }
    }
  }
  .btn-left {
    margin-left: 6px;
  }
  .close-btn {
    display: none;
  }
  @include mobileStyles {
    .close-btn {
      display: inline-flex;
      margin-left: 30px;
      vertical-align: middle;
    }
    .btn-left {
      margin-left: 0;
    }
    .back-btn {
      display: none;
    }
  }
  @media screen and (max-width: $phone-change-popup-mobile-bp) {
    .popup-input {
      margin-left: -15px;
      width: calc(100% + 30px);
    }
    .phone-change-popup {
      &__wrap {
        width: auto;
      }
      &__stages-legend {
        @include incrSelectorWeight(1) {
          width: calc(100vw + 50px);
        }
      }
      &__resend {
        margin-top: 30px;
      }
    }
    .new-liner {
      display: none;
    }
    .stages-legend {
      &_stage2 {
        @include incrSelectorWeight(1) {
          $w: 261px;
          min-width: $w;
          width: $w;
          margin-left: -172px;
        }
      }
    }
  }
  @media screen and (max-width: 339px) {
    .phone-change__resend-text {
      max-width: 220px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/common/PhoneChangePopup.vue